import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  createWorkspaceInviteToken,
  listWorkspaceInviteTokens,
  revokeWorkspaceInviteToken,
} from "@services/api";
import { useNotification } from "@shared/contexts/NotificationContext";
import type { WorkspaceInviteToken } from "@/types/models";

const inviteUrl = (token: string) =>
  `${window.location.origin}/workspaces/join/${token}`;

const isTokenActive = (t: WorkspaceInviteToken) => {
  if (t.revoked_at) return false;
  if (t.expires_at && new Date(t.expires_at) < new Date()) return false;
  return true;
};

const formatExpiry = (t: WorkspaceInviteToken) => {
  if (t.revoked_at) return "Revoked";
  if (!t.expires_at) return "No expiration";
  const exp = new Date(t.expires_at);
  if (exp < new Date()) return "Expired";
  return `Expires ${exp.toLocaleDateString()}`;
};

const EXPIRY_CHOICES: { label: string; days: number }[] = [
  { label: "1 day", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "No expiration", days: 0 },
];

interface Props {
  workspaceId: string;
}

const WorkspaceInviteLinksPanel = ({ workspaceId }: Props) => {
  const { showSuccess, showError } = useNotification();
  const [tokens, setTokens] = useState<WorkspaceInviteToken[]>([]);
  const [busy, setBusy] = useState(false);
  const [expiryDays, setExpiryDays] = useState<number>(7);

  const fetchTokens = useCallback(async () => {
    if (!workspaceId) return;
    const result = await listWorkspaceInviteTokens(workspaceId);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    setTokens(result.data);
  }, [workspaceId, showError]);

  useEffect(() => {
    void fetchTokens();
  }, [fetchTokens]);

  const handleCreate = async () => {
    setBusy(true);
    const result = await createWorkspaceInviteToken(workspaceId, expiryDays);
    setBusy(false);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    showSuccess("Invite link created");
    await fetchTokens();
  };

  const handleCopy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      showSuccess("Invite link copied");
    } catch {
      showError("Couldn't copy to clipboard");
    }
  };

  const handleRevoke = async (token: string) => {
    if (
      !window.confirm(
        "Revoke this invite link? Anyone with the URL will no longer be able to join."
      )
    ) {
      return;
    }
    setBusy(true);
    const result = await revokeWorkspaceInviteToken(workspaceId, token);
    setBusy(false);
    if (!result.success) {
      showError(result.error.message);
      return;
    }
    showSuccess("Invite link revoked");
    await fetchTokens();
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Invite links
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate a shareable link. Anyone who opens it and signs in joins
        this workspace as a contributor. Revoked links stop working
        immediately.
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="workspace-invite-expiry-label">
            Expires after
          </InputLabel>
          <Select
            labelId="workspace-invite-expiry-label"
            label="Expires after"
            value={expiryDays}
            onChange={(e) => setExpiryDays(Number(e.target.value))}
          >
            {EXPIRY_CHOICES.map((c) => (
              <MenuItem key={c.days} value={c.days}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" disabled={busy} onClick={handleCreate}>
          Generate invite link
        </Button>
      </Stack>

      {tokens.length > 0 && (
        <TableContainer component={Box}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Link</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((t) => {
                const active = isTokenActive(t);
                return (
                  <TableRow key={t.token}>
                    <TableCell
                      sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                    >
                      {inviteUrl(t.token)}
                    </TableCell>
                    <TableCell>{formatExpiry(t)}</TableCell>
                    <TableCell align="right">
                      {active && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleCopy(t.token)}
                            aria-label="Copy invite link"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={busy}
                            onClick={() => handleRevoke(t.token)}
                            aria-label="Revoke invite link"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default WorkspaceInviteLinksPanel;
